import * as React from 'react';
import { Select, Spin } from 'src/design-system/components/atoms';
import { useTranslation } from 'react-i18next';
import { searchStrategy as getSearchStrategy } from '../pages/GenericConsignment/CustomFilters/utils';
import useDebounce from '../pages/TMSDashboard/hooks/useDebounce';
import { connect } from 'react-redux';

export interface BackendSearchFieldConfig {
  id?: string;
  prettyName?: string;
  formUIComponent?: {
    searchStrategy: string;
    searchSelectKey?: string;
    searchThresholdLength?: number;
    multiselect?: boolean;
  };
}

export interface BackendSearchFieldProps {
  fieldConfig: BackendSearchFieldConfig;
  value?: any;
  onChange: (value: any) => void;
  style?: React.CSSProperties;
  additionalSearchParams?: any[];
  hubId?: string;
}

const BackendSearchField: React.FC<BackendSearchFieldProps> = ({
  fieldConfig,
  value,
  onChange,
  style,
  hubId,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [options, setOptions] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);

  const debouncedQuery = useDebounce(searchQuery, 300);

  const {
    id,
    prettyName,
    formUIComponent,
  } = fieldConfig;

  const {
    searchStrategy,
    searchSelectKey,
    searchThresholdLength = 3,
    multiselect
  } = formUIComponent || {};

  const handleSearch = async (searchValue: string) => {
    if (!searchValue || searchValue.length < searchThresholdLength) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const searchFn = getSearchStrategy(searchStrategy, searchSelectKey);
    const results = await searchFn(searchValue, hubId);

    if (Array.isArray(results) && results.length > 0) {
      const formattedResults = results.map((item: any) => ({
        value: item.key,
        label: item.label,
      }));
      setOptions(formattedResults);
    } else {
      setOptions([]);
    }
    setLoading(false);
  };

  React.useEffect(() => {
    if (debouncedQuery !== undefined && debouncedQuery !== '') {
      handleSearch(debouncedQuery);
    }
  }, [debouncedQuery]);

  return (
    <Select
      id={id}
      mode={multiselect ? 'multiple' : undefined}
      showSearch
      allowClear={true}
      placeholder={prettyName}
      filterOption={false}
      onSearch={setSearchQuery}
      onChange={onChange}
      value={value}
      options={options}
      loading={loading}
      style={style || { width: '100%' }}
      notFoundContent={loading ? <Spin size="small" /> :
        ((searchQuery && searchQuery.length >= searchThresholdLength) ? t('No results found') : t('Type to search'))}
    />
  );
};

const mapStateToProps = ({ cachedData }, ownProps) => {
  return {
    hubId: cachedData.currHub?.id,
  };
};

export default connect(mapStateToProps)(BackendSearchField);
